import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/atoms";
import AIReviewDetailTemplate from "components/templates/AIReviewDetailTemplate";
import { fetchFullReport } from "services/caringnoteApi";
import { useCaringNoteReport } from "./useCaringNoteReport";
import { toPartnerReview } from "./toPartnerReview";
import CaringNoteStatus from "./components/CaringNoteStatus";

/**
 * 독립뷰(report) — `/caringnote/report/:token` (계획 P6 · §2·T2).
 *
 * 어드민 팝업(`/admin/ai-reviews/:reviewId/popup`)과 **같은 3단 뷰어**(`AIReviewDetailTemplate`)를
 * 새 창에서 **로그인 없이** 연다(계약 §6·2026-08-07 미팅 확정 4).
 * - `hideHeader`로 템플릿의 내부 헤더(핀·메모·`보고서 #{id}`)를 제거하고, `readOnly`로 편집 유도
 *   문구까지 렌더 차단한다 → 쓰기 UI가 하나도 없다(§0-ⓑ). CSS 은닉이 아니라 렌더 자체를 막는다.
 * - `patientSnap`은 **응답에 포함된다**(계약 v1.4 §6). 약사가 상담 중 판단하려면 필요하고 케어링노트가
 *   이미 보유한 정보다. 내부 식별자(`id`·`userId`)는 서버가 걷어내고 보낸다. 값이 없는 응답에서도
 *   템플릿이 가드해 죽지 않는다(§0-ⓒ). (2026-08-31: 탑재용 `embed`도 `patientSnap`을 받아
 *   환자정보·처방표를 조건부로 보여준다 — CaringNoteEmbedPage.)
 * - 프레이밍 금지 경로(CSP frame-ancestors 'none') — 새 창 전용.
 * - 어떤 오류에도 로그인 화면으로 보내지 않는다(P5 `useCaringNoteReport`·`CaringNoteStatus` 재사용).
 *
 * ⚠️ 3단 뷰어는 약품 이미지(외부 S3)를 다시 로드한다(§3-4). 요청 Referer로 URL 토큰이 새지 않도록
 *    `customHttp.yml`이 이 경로에 `Referrer-Policy: no-referrer`를 건다 — **단 배포 후 실증 전까지 미검증**(R-5).
 */

const Page = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  overflow: hidden;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

// 오류/로딩/빈 상태를 본문 중앙에 배치.
const StatusBody = styled(Body)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CaringNoteReportPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { state, retry } = useCaringNoteReport(fetchFullReport, token);

  // 헤더(제목·닫기)를 두지 않는다(2026-08-14 oscar). 이 화면은 케어링노트가 window.open으로
  // 여는 새 창이라 브라우저 탭 닫기로 충분하고, 우리 제목줄은 케어링노트 화면 맥락에서 군더더기다.
  // 어드민 팝업의 껍데기를 따라 넣었던 것을 걷어냈다.
  const shell = (body: React.ReactNode, centered = false) => {
    const BodyWrap = centered ? StatusBody : Body;
    return (
      <Page>
        <BodyWrap>{body}</BodyWrap>
      </Page>
    );
  };

  if (state.phase === "loading") {
    return shell(<CaringNoteStatus variant="loading" />, true);
  }

  if (state.phase === "error" || state.phase === "empty") {
    const kind = state.phase === "empty" ? "empty" : state.kind;
    return shell(<CaringNoteStatus variant="status" kind={kind} onRetry={retry} />, true);
  }

  return shell(
    // pillsData를 넘겨 3단 뷰어의 자체 /drugs/pills fetch(비로그인 401)를 건너뛰고, 서버가 준 성상을
    // 무가공 그대로 렌더한다(D-4·D-9). 미배포 전이엔 pills 부재 → [](요청 0건, 성상 없음)로 안전 폴백.
    <AIReviewDetailTemplate
      review={toPartnerReview(state.data)}
      pillsData={state.data.pills ?? []}
      hideHeader
      readOnly
    />
  );
};

export default CaringNoteReportPage;
